// Propozycje produktów dla „Asystenta doboru" (dymki upsell przy sekcjach).
// Korzysta z ISTNIEJĄCYCH endpointów listingów — front tylko wybiera pierwszy
// (najtańszy) pasujący produkt. Miejsce pod przyszły upsell dla firm (promowane oferty).
import { classifyAvailability } from '../utils';
import { PROPONOWANE_OFERTY } from '../config/proponowaneWAsystencie';
import { apiFetch } from './apiFetch';

const apiUrl = process.env.REACT_APP_API_URL || '/api/';

const getJson = async (path) => {
  try {
    const res = await apiFetch(`${apiUrl}${path}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/* ═══════════════════════════════════════════════════════════════════════════
   OFERTA WSKAZANA RĘCZNIE (patrz config/proponowaneWAsystencie.js)

   Każda z funkcji `recommend*` najpierw sięga po wskazaną ofertę i sprawdza, czy
   pasuje do filtrów użytkownika. Dopiero gdy jej nie ma albo nie pasuje — leci
   dotychczasowe wyszukiwanie. Dzięki temu podmiana promowanego produktu to zmiana
   jednej liczby w konfiguracji, a nie zmiana logiki w trzech miejscach.
   ═══════════════════════════════════════════════════════════════════════════ */

const ENDPOINT_OFERTY = {
  panel: 'get_panel',
  inwerter: 'get_inverter',
  magazyn: 'get_magazine',
};

/**
 * Wskazana oferta albo `null`. Bez `model_id` zwracamy `null` mimo poprawnej
 * odpowiedzi: silnik doboru pracuje na warstwie modeli, więc oferty bez modelu
 * nie da się przypiąć do zestawu, a dymek z martwym checkboxem jest gorszy
 * niż dymek z innym produktem.
 */
const wskazanaOferta = async (kategoria) => {
  const id = PROPONOWANE_OFERTY[kategoria];
  if (!id) return null;
  const d = await getJson(`${ENDPOINT_OFERTY[kategoria]}/${id}`);
  if (!d || d.error || d.id == null || !d.model_id) return null;
  return d;
};

/** Czy oferta jest dostępna — sprawdzane lokalnie, bo pobieramy ją po id, z pominięciem filtra API. */
const jestDostepna = (oferta) => classifyAvailability(oferta?.availability).tone !== 'out';

const wZakresie = (v, min, max) => (
  v != null && (min == null || v >= min) && (max == null || v <= max)
);

// Proponowany moduł: najpierw oferta wskazana w konfiguracji, potem — gdyby jej
// nie było — oferta sprzedawcy partnerskiego. Wyszukiwanie zostaje jako zapas,
// bo identyfikatory różnią się między środowiskami (na produkcji `/get_panel/1`
// nie istniał i dymek się nie pokazywał).
// Preferujemy moduły o sensownej mocy; przy braku trafień luzujemy warunki.
const PARTNER_SELLER = 'Twoja fotowoltaika';

const panelQuery = (params) => {
  const q = new URLSearchParams({ available_only: 'true', order_by_price: 'asc', ...params });
  return getJson(`panels/?${q.toString()}`);
};

export const recommendPanel = async () => {
  // Sekcja modułów nie ma filtrów produktowych (pyta o moc instalacji, nie o model),
  // więc wskazana oferta obowiązuje bez dodatkowych warunków.
  const wskazana = await wskazanaOferta('panel');
  if (wskazana) return wskazana;

  for (const params of [
    { sellers: PARTNER_SELLER, minPower: 500 },
    { sellers: PARTNER_SELLER, minPower: 350 },
    { minPower: 500 },
  ]) {
    const items = (await panelQuery(params))?.items || [];
    // Kolejność preferencji: z modelem I zdjęciem (da się przypiąć do doboru) >
    // z modelem > ze zdjęciem > cokolwiek. Silnik pracuje na warstwie modeli,
    // więc bez model_id checkbox „wybierz do zestawu" nie ma czego wysłać.
    const hit = items.find((x) => x.model_id && x.img_url)
      || items.find((x) => x.model_id)
      || items.find((x) => x.img_url)
      || items[0];
    if (hit) return hit;
  }
  return null;
};

// Falownik: pasujące oferty sprzedawcy „Twoja fotowoltaika" (typ + zakres mocy + dostępność).
export const recommendInverter = async ({ type, minPower, maxPower, availableOnly }) => {
  // Wskazany falownik tylko wtedy, gdy odpowiada na to, o co pyta użytkownik.
  // Propozycja 8 kW przy ustawionym zakresie 3–5 kW nie jest propozycją, tylko
  // reklamą — i podważa zaufanie do reszty doboru.
  const wskazany = await wskazanaOferta('inwerter');
  if (wskazany
      && (!type || String(wskazany.type || '') === String(type))
      && wZakresie(wskazany.power, minPower, maxPower)
      && (!availableOnly || jestDostepna(wskazany))) {
    return wskazany;
  }

  const q = new URLSearchParams();
  q.set('sellers', 'Twoja fotowoltaika');
  if (type) q.set('types', type);
  // Sanity: realny falownik ma moc (mikroinwertery ~0,3 kW) — ucina rekordy-śmieci z power=0.
  q.set('minPower', minPower != null ? minPower : 0.3);
  if (maxPower != null) q.set('maxPower', maxPower);
  if (availableOnly) q.set('available_only', 'true');
  q.set('order_by_price', 'asc');
  const d = await getJson(`inverters/?${q.toString()}`);
  const items = d?.items || [];
  // TA SAMA kolejność preferencji co przy modułach: najpierw oferta z modelem
  // i zdjęciem, potem z modelem, potem cokolwiek. Bez `model_id` znika checkbox
  // „wybierz do zestawu" — silnik pracuje na warstwie modeli, więc nie ma czego
  // przypiąć. Sama najtańsza oferta bywa rekordem bez modelu i dymek falownika
  // tracił wtedy checkbox, choć moduły i magazyn go miały. Zmierzone.
  return items.find((x) => x.model_id && x.img_url)
    || items.find((x) => x.model_id)
    || items[0]
    || null;
};

// Magazyn: najtańszy „Moduł Bateryjny" w zakresie pojemności. Przy wysokonapięciowym
// dokładamy BMS TEGO SAMEGO producenta (brak listy kompatybilności modeli — dobór po
// producencie). Gdy availableOnly: magazyn i BMS muszą być dostępne; HV bez dostępnego
// BMS producenta -> próbujemy kolejny magazyn z listy.
const isHV = (v) => (v || '').toLowerCase().startsWith('wysok');

// Akcesoria błędnie otypowane w danych jako „Moduł Bateryjny" (podstawy, okablowanie,
// skrzynki itp.) — wycinamy po nazwie, żeby dymek nie proponował podkładki za 3 zł.
const JUNK_RE = /podstaw|okablowani|skrzynk|przy.{1,2}czeniow|junction|kabel|przew.d|wspornik|uchwyt|kontroler|\bbcu\b|akcesor|zestaw monta/i;

// Dobór BMS do modelu magazynu = BACKEND (źródło prawdy, model-level: seria/producent).
// Ten sam mechanizm co finalny zestaw (/suggest), więc dymek i zestaw są spójne.
const BMS_BASE = process.env.REACT_APP_KITS_API_URL || '/api/v1/kits';
const bmsForModel = async (modelId, availableOnly) => {
  try {
    const res = await apiFetch(`${BMS_BASE}/battery-bms/${modelId}${availableOnly ? '?require_available=true' : ''}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Domknięcie pojedynczego magazynu: czy da się go pokazać razem z wymaganym BMS.
 *
 * Zwraca gotową odpowiedź dymka albo `null` — `null` znaczy „ten magazyn wymaga
 * BMS, którego nie umiemy dobrać, spróbuj następnego". Wyjęte z pętli, bo tej samej
 * logiki potrzebuje oferta wskazana ręcznie; dwie kopie rozjechałyby się przy
 * pierwszej zmianie zasad doboru BMS.
 */
const domknijMagazyn = async (mag, availableOnly) => {
  if (!isHV(mag.voltage)) return { magazine: mag, bms: null, bmsRequired: false };
  if (!mag.model_id) return null;
  const reco = await bmsForModel(mag.model_id, availableOnly);
  if (reco && !reco.required) return { magazine: mag, bms: null, bmsRequired: false };
  if (reco && reco.bms) {
    return {
      magazine: mag,
      bmsRequired: true,
      bms: {
        manufacturer: reco.bms.manufacturer,
        name: reco.bms.name,
        price: reco.bms.price_pln,
        // Parametry z karty sterownika — sterownik nie ma własnej strony produktu,
        // więc asystent jest jedynym miejscem, gdzie da się je pokazać.
        electrical_spec: reco.bms.electrical_spec || null,
      },
    };
  }
  return null;
};

export const recommendBattery = async ({ minCap, maxCap, availableOnly }) => {
  // Wskazany magazyn tylko w zamówionym zakresie pojemności — tak samo jak przy
  // falowniku, propozycja ma odpowiadać na pytanie użytkownika.
  const wskazany = await wskazanaOferta('magazyn');
  if (wskazany
      && wZakresie(wskazany.capacity, minCap, maxCap)
      && (!availableOnly || jestDostepna(wskazany))) {
    const domkniety = await domknijMagazyn(wskazany, availableOnly);
    if (domkniety) return domkniety;
  }

  const q = new URLSearchParams();
  q.set('types', 'Moduł Bateryjny');
  // Sanity: realny moduł bateryjny ma pojemność (filtr ucina też rekordy-śmieci bez
  // capacity, np. akcesoria błędnie otypowane) i niezerową cenę.
  q.set('minCapacity', minCap != null ? minCap : 1);
  q.set('minPrice', 1);
  if (maxCap != null) q.set('maxCapacity', maxCap);
  if (availableOnly) q.set('available_only', 'true');
  q.set('order_by_price', 'asc');
  const d = await getJson(`magazines/?${q.toString()}`);
  let items = (d?.items || []).filter((x) => !JUNK_RE.test(x.name || ''));
  // Preferuj oferty POWIĄZANE Z MODELEM (model_id) — tylko takie da się przypiąć do
  // doboru (silnik pracuje na warstwie modeli), a rekordy-śmieci zwykle modelu nie mają.
  const modeled = items.filter((x) => x.model_id);
  if (modeled.length) items = modeled;

  let firstMissing = null;
  for (const mag of items.slice(0, 8)) {
    const domkniety = await domknijMagazyn(mag, availableOnly);
    if (domkniety) return domkniety;
    // Wymaga BMS, ale nie dobrano konkretnego produktu — spróbuj kolejny magazyn,
    // a jak żaden nie da się dopełnić, pokaż pierwszy z adnotacją „bmsMissing".
    if (!firstMissing && mag.model_id) firstMissing = mag;
  }
  if (firstMissing) {
    return { magazine: firstMissing, bms: null, bmsRequired: true, bmsMissing: true };
  }
  if (items.length) {
    return { magazine: items[0], bms: null, bmsRequired: isHV(items[0].voltage), bmsMissing: isHV(items[0].voltage) };
  }
  return null;
};

/**
 * Przypięty produkt z ID modelu (wejście ze strony produktu: kafelek „Asystent doboru”, 2026-10-08).
 * Silnik doboru przyjmuje `pinned_model_id` i buduje zestaw wokół tego modelu; tu składamy to, co
 * pokazuje dymek: najtańszą ofertę modelu (najlepiej ze zdjęciem), a przy magazynie także BMS, tak
 * samo jak dla propozycji. `null`, gdy model nie ma oferty z ceną (silnik i tak by go odrzucił).
 */
const ENDPOINT_MODELU = { panel: 'get_panel_model', inwerter: 'get_inverter_model', magazyn: 'get_magazine_model' };

export const przypietyZModelu = async (kategoria, modelId) => {
  if (!ENDPOINT_MODELU[kategoria] || !Number(modelId)) return null;
  const m = await getJson(`${ENDPOINT_MODELU[kategoria]}/${Number(modelId)}`);
  if (!m || m.error) return null;
  const oferty = (m.offers || []).filter((o) => Number(o.price) > 0).sort((a, b) => Number(a.price) - Number(b.price));
  const o = oferty.find((x) => x.img_url) || oferty[0];
  if (!o) return null;
  const oferta = {
    ...o,
    model_id: Number(modelId),
    img_url: o.img_url || m.img_url || null,
    power: o.power ?? m.power ?? null,
    capacity: o.capacity ?? m.capacity ?? null,
    voltage: o.voltage ?? m.voltage ?? null,
  };
  if (kategoria !== 'magazyn') return oferta;
  return (await domknijMagazyn(oferta, false)) || { magazine: oferta, bms: null, bmsRequired: true, bmsMissing: true };
};
